import { LoiForm } from './loi-form';
import {
    DEFERRED_FIXED_PAYMENT_COLUMN_LABELS,
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS,
    JV_EQUITY_COLUMN_LABELS,
    CORPORATE_EQUITY_COLUMN_LABELS,
} from './term-form';
import { hasValueForLinkedField } from './has-value-for-linked-field';

export interface LoiFormErrors {
    termFormFields: Set<string>;
    legalIssueIds: Set<string>;
    additionalCovenantIds: Set<string>;
    loiFileRequired?: boolean;
}

export interface LoiFormValidationOptions {
    documentDetailsId?: string;
}

function isNonEmpty(value: string | number | null | undefined): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
}

function isNonNull(value: number | null | undefined): boolean {
    return value !== null && value !== undefined;
}

export function validateLoiForm(formData: LoiForm, options?: LoiFormValidationOptions): LoiFormErrors {
    const termFormFields = new Set<string>();
    const legalIssueIds = new Set<string>();
    const additionalCovenantIds = new Set<string>();
    const loiFileRequired = !options?.documentDetailsId;

    const termForm = formData.termForm;
    if (termForm) {
        if (!isNonEmpty(termForm.loiExpirationDate)) termFormFields.add('loiExpirationDate');
        if (!isNonNull(termForm.exclusivityPeriodMonth)) termFormFields.add('exclusivityPeriodMonth');

        if (!isNonEmpty(termForm.propertyPurchased?.practicePropertyPurchased)) termFormFields.add('practicePropertyPurchased');
        if (!isNonEmpty(termForm.propertyPurchased?.practiceRealPropertyInclude)) termFormFields.add('practiceRealPropertyInclude');

        const summary = termForm.purchasePriceSummary;
        const cashAtCloser = summary?.cashAtCloser;
        if (!isNonNull(cashAtCloser?.cashAtClose)) termFormFields.add('cashAtClose');
        if (!isNonNull(cashAtCloser?.workingCapitalHoldback?.value)) termFormFields.add('workingCapitalHoldbackValue');
        if (!isNonNull(cashAtCloser?.workingCapitalHoldback?.monthsHold)) termFormFields.add('workingCapitalHoldbackMonths');
        if (!isNonNull(cashAtCloser?.netCashAtCloser)) termFormFields.add('netCashAtCloser');

        const dfpRows = summary?.deferredFixedPayments?.rows ?? [];
        if (!dfpRows.length || !isNonNull(dfpRows[0]?.amount)) termFormFields.add('deferredFixedPayments');

        const dvpRows = summary?.deferredVariablePayments?.rows ?? [];
        if (!dvpRows.length || !isNonNull(dvpRows[0]?.valueAtClosing)) termFormFields.add('deferredVariablePayments');

        if (!isNonNull(summary?.jvEquity?.row?.valueAtClosing)) termFormFields.add('jvEquityValueAtClosing');
        if (!isNonNull(summary?.corporateEquity?.row?.valueAtClosing)) termFormFields.add('corporateEquityValueAtClosing');
        if (!isNonNull(summary?.other)) termFormFields.add('other');

        const dfpTotal = dfpRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        if (dfpTotal > 0) {
            const L = DEFERRED_FIXED_PAYMENT_COLUMN_LABELS;
            for (let i = 0; i < dfpRows.length; i++) {
                const r = dfpRows[i];
                if (!isNonEmpty(r.paymentTiming)) termFormFields.add(`dfp:${i}:${L.paymentTiming}`);
                if (!isNonNull(r.amount)) termFormFields.add(`dfp:${i}:${L.amount}`);
                if (!isNonEmpty(r.annualizedInterestAccrualRate)) termFormFields.add(`dfp:${i}:${L.annualizedInterestAccrualRate}`);
                if (!isNonEmpty(r.contingent)) termFormFields.add(`dfp:${i}:${L.contingent}`);
                if (!isNonEmpty(r.contingencyDependentVariableValue)) termFormFields.add(`dfp:${i}:${L.contingencyDependentVariable}`);
                if (!isNonNull(r.contDvMinimumValue)) termFormFields.add(`dfp:${i}:${L.contDvMinimumValue}`);
                if (!isNonEmpty(r.employmentContingency)) termFormFields.add(`dfp:${i}:${L.employmentContingency}`);
                if (!isNonEmpty(r.tailPeriodForEmployment)) termFormFields.add(`dfp:${i}:${L.tailPeriodForEmployment}`);
            }
        }

        const dvpTotal = dvpRows.reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
        if (dvpTotal > 0) {
            const L = DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS;
            for (let i = 0; i < dvpRows.length; i++) {
                const r = dvpRows[i];
                if (!isNonEmpty(r.paymentTiming)) termFormFields.add(`dvp:${i}:${L.paymentTiming}`);
                if (!isNonEmpty(r.type)) termFormFields.add(`dvp:${i}:${L.type}`);
                if (!isNonNull(r.valueAtClosing)) termFormFields.add(`dvp:${i}:${L.valueAtClosing}`);
                if (!isNonEmpty(r.calculationDependentVariable)) termFormFields.add(`dvp:${i}:${L.calculationDependentVariable}`);
                if (!isNonNull(r.calcDvBaselineValue)) termFormFields.add(`dvp:${i}:${L.calcDvBaselineValue}`);
                if (!isNonEmpty(r.calculationMultiple)) termFormFields.add(`dvp:${i}:${L.calculationMultiple}`);
                if (!isNonEmpty(r.maximumPayment)) termFormFields.add(`dvp:${i}:${L.maximumPayment}`);
                if (!isNonEmpty(r.employmentContingency)) termFormFields.add(`dvp:${i}:${L.employmentContingency}`);
                if (!isNonEmpty(r.tailPeriodForEmploymentContingency)) termFormFields.add(`dvp:${i}:${L.tailPeriodForEmploymentContingency}`);
                if (!isNonEmpty(r.penaltyMultiple)) termFormFields.add(`dvp:${i}:${L.penaltyMultiple}`);
                if (!isNonEmpty(r.distributionPeriodicity)) termFormFields.add(`dvp:${i}:${L.distributionPeriodicity}`);
            }
        }

        const jvRow = summary?.jvEquity?.row;
        if (jvRow && isNonNull(jvRow.valueAtClosing) && Number(jvRow.valueAtClosing) !== 0) {
            const L = JV_EQUITY_COLUMN_LABELS;
            if (!isNonEmpty(jvRow.paymentTiming)) termFormFields.add(`jv:0:${L.paymentTiming}`);
            if (!isNonEmpty(jvRow.calculationDependentVariable)) termFormFields.add(`jv:0:${L.calculationDependentVariable}`);
            if (!isNonNull(jvRow.calcDvBaselineValue)) termFormFields.add(`jv:0:${L.calcDvBaselineValue}`);
            if (!isNonEmpty(jvRow.calculationMultiple)) termFormFields.add(`jv:0:${L.calculationMultiple}`);
            if (!isNonEmpty(jvRow.employmentContingency)) termFormFields.add(`jv:0:${L.employmentContingency}`);
            if (!isNonEmpty(jvRow.tailPeriodForEmploymentContingency)) termFormFields.add(`jv:0:${L.tailPeriodForEmploymentContingency}`);
            if (!isNonEmpty(jvRow.penaltyMultiple)) termFormFields.add(`jv:0:${L.penaltyMultiple}`);
            if (!isNonEmpty(jvRow.distributionPeriodicity)) termFormFields.add(`jv:0:${L.distributionPeriodicity}`);
        }

        const corpRow = summary?.corporateEquity?.row;
        if (corpRow && isNonNull(corpRow.valueAtClosing) && Number(corpRow.valueAtClosing) !== 0) {
            const L = CORPORATE_EQUITY_COLUMN_LABELS;
            if (!isNonEmpty(corpRow.expectedTimingToRecap)) termFormFields.add(`corp:0:${L.expectedTimingToRecap}`);
            if (!isNonEmpty(corpRow.expectedPayoutAsMultipleOfCurrentValue)) termFormFields.add(`corp:0:${L.expectedPayoutAsMultipleOfCurrentValue}`);
        }

        const gdpi = termForm.generalDeferredPaymentsInformation;
        if (!isNonEmpty(gdpi?.paymentObligor)) termFormFields.add('paymentObligor');
        if (!isNonEmpty(gdpi?.assetsHeldByObligor)) termFormFields.add('assetsHeldByObligor');
        if (!isNonEmpty(gdpi?.interestAppliedToDeferredPayments)) termFormFields.add('interestAppliedToDeferredPayments');

        const lease = termForm.leaseTerms;
        if (!isNonEmpty(lease?.termOfLease)) termFormFields.add('termOfLease');
        if (!isNonEmpty(lease?.leaseType)) termFormFields.add('leaseType');
        if (!isNonEmpty(lease?.leaseRate)) termFormFields.add('leaseRate');
        if (!isNonEmpty(lease?.leaseRateEscalation)) termFormFields.add('leaseRateEscalation');
        if (!isNonEmpty(lease?.leaseRateUponExtension)) termFormFields.add('leaseRateUponExtension');
    } else {
        termFormFields.add('loiExpirationDate');
    }

    const sectionById = new Map(formData.legalIssueSections.map((s) => [s.id, s]));

    for (const item of formData.legalIssues) {
        const section = item.sectionId != null ? sectionById.get(item.sectionId) : undefined;
        if (section?.termsFormLinkedField != null && !hasValueForLinkedField(formData.termForm, section.termsFormLinkedField)) continue;
        if (item.showValueInput !== false && !isNonEmpty(item.value)) {
            legalIssueIds.add(item.id);
        }
    }

    for (const item of (formData.additionalCovenants ?? [])) {
        const section = item.sectionId != null ? sectionById.get(item.sectionId) : undefined;
        if (section?.termsFormLinkedField != null && !hasValueForLinkedField(formData.termForm, section.termsFormLinkedField)) continue;
        if (item.showValueInput !== false && !isNonEmpty(item.value)) {
            additionalCovenantIds.add(item.id);
        }
    }

    return { termFormFields, legalIssueIds, additionalCovenantIds, loiFileRequired };
}

export function hasLoiFormErrors(errors: LoiFormErrors): boolean {
    return errors.termFormFields.size > 0
        || errors.legalIssueIds.size > 0
        || errors.additionalCovenantIds.size > 0
        || !!errors.loiFileRequired;
}
